import { DateTime } from 'luxon'
import { compose, path, pick, last, propOr } from 'ramda'

import { FlightLabelStatus } from '../../../src/ui/labels/FlightLabel.jsx'
import { formatTime, msToMin } from '../../../src/utils/date.js'

export const FlightType = {
  ARRIVAL: 'arr',
  DEPARTURE: 'dep'
}

export function mapFlightListItem (nowDate, flight, T, tz, locale) {
  const {
    flightId,
    carrierFsCode,
    flightNumber,
    airline,
    flightType,
    departureAirport,
    arrivalAirport,
    airportResources,
    gatePoi,
    status: statusCode
  } = flight

  const rawDateTime = selectRawDateTime(flight)
  const { localTime, localOldTime } = formatToLocalDateTimes(rawDateTime, tz) // local refers to the venue time
  const status = mapFlightStatus(flightType, statusCode, nowDate, localTime, localOldTime)
  const datePlaceholder = flightType === FlightType.DEPARTURE
    ? 'flightDetails:Departs _date_'
    : 'flightDetails:Arrives _date_'

  return {
    flightId,
    name: `${propOr('', 'name', airline)} - ${carrierFsCode} ${flightNumber}`,
    status,
    statusText: T(`flightDetails:${getFlightStatusText(status)}`),
    baggageClaim: flightType === FlightType.ARRIVAL ? prepareBaggageClaim(airportResources.arrivalBagClaim, T) : undefined,
    connectionAirport: prepareAirportLabel(flightType, departureAirport, arrivalAirport, T),
    time: getLocalFormattedTime(localTime, tz, locale),
    oldTime: getLocalFormattedTime(localOldTime, tz, locale),
    date: T(datePlaceholder, { date: getLocalFormattedDate(localTime, tz, locale) }),
    dateStamp: localTime.toMillis(),
    gate: formatGate(gatePoi),
    gateLabel: T('flightDetails:Gate')
  }
}

// todo create test when airline is undefined
export function mapFlightDetails (nowDate, flight, T, tz, locale) {
  const {
    flightNumber,
    flightType,
    departureAirport,
    arrivalAirport,
    airportResources,
    flightStatusUpdates,
    carrierFsCode,
    gatePoi,
    airline,
    status: statusCode
  } = flight

  const pickAirportData = pick(['city', 'iata'])
  const rawDateTime = selectRawDateTime(flight)
  const { localTime, localOldTime } = formatToLocalDateTimes(rawDateTime, tz) // local refers to the venue time
  const status = mapFlightStatus(flightType, statusCode, nowDate, localTime, localOldTime)
  const flightTypeLabel = flightType === FlightType.DEPARTURE
    ? 'Departure'
    : 'Arrival'

  return {
    flightType,
    flightTypeLabel: T(`flightDetails:${flightTypeLabel}`),
    name: `${propOr('', 'name', airline)} - ${carrierFsCode} ${flightNumber}`,
    status,
    statusText: T(`flightDetails:${getFlightStatusText(status)}`),
    baggageClaim: flightType === FlightType.ARRIVAL ? prepareBaggageClaim(airportResources.arrivalBagClaim, T) : undefined,
    departure: pickAirportData(departureAirport),
    arrival: pickAirportData(arrivalAirport),
    connectionAirport: prepareAirportLabel(flightType, departureAirport, arrivalAirport, T),
    flightIn: prepareFlightIn(status, flightType, nowDate, localTime, T),
    time: getLocalFormattedTime(localTime, tz, locale),
    oldTime: getLocalFormattedTime(localOldTime, tz, locale),
    date: getLocalFormattedDate(localTime, tz, locale),
    lastUpdated: prepareLastUpdated(nowDate, flightStatusUpdates, T),
    flightDuration: '',
    gateLabel: T('flightDetails:Gate'),
    gate: formatGate(gatePoi),
    tags: [],
    flightIconBaseName: flightType === FlightType.ARRIVAL ? 'arrivals' : 'departures'
  }
}

// used to sort flights in ASCENDING order of flight arrival/departure time
export const flightComparator = (flight1, flight2) => flight1.dateStamp - flight2.dateStamp

const formatGate = (gate) => {
  if (!gate) return { name: '-' }
  const name = last(gate.name.split(' '))
  return { ...gate, name }
}

const prepareFlightIn = (status, flightType, nowDate, flightDate, T) => {
  if (status === FlightLabelStatus.DEPARTED) return T('flightDetails:Departed')
  else if (status === FlightLabelStatus.ARRIVED) return T('flightDetails:Arrived')
  else if (status === FlightLabelStatus.CANCELLED) return null

  const prefix = flightType === FlightType.DEPARTURE ? 'Departs' : 'Arrives'
  const minutes = minutesBetween(nowDate, flightDate)
  return T(`flightDetails:${prefix} in _minutes_ minute`, { count: minutes })
}

const prepareLastUpdated = (nowDate, flightStatusUpdates, T) => {
  const updateDate = compose(path(['updatedAt', 'dateUtc']), last)(flightStatusUpdates)
  const minutes = minutesBetween(nowDate, getLocalGMTDate(updateDate))

  if (minutes < 10) return T('flightDetails:Last updated a few minutes ago')
  else if (minutes < 60) return T('flightDetails:Last updated _minutes_ minutes ago', { minutes })
  else return T('flightDetails:Last updated over an hour ago')
}

/**
* Check minutes difference between two Luxon dates.
* @param {DateTime} firstDate
* @param {DateTime} secondDate
*/
const minutesBetween = (firstDate, secondDate) => {
  const ms = Math.abs(firstDate - secondDate)
  return msToMin(ms)
}

const selectRawDateTime = ({
  flightType,
  operationalTimes,
  departureDate,
  arrivalDate
}) => flightType === FlightType.DEPARTURE
  ? prepareRawDateTime(departureDate, operationalTimes.estimatedGateDeparture)
  : prepareRawDateTime(arrivalDate, operationalTimes.estimatedGateArrival)

const prepareRawDateTime = (dateTime, estimatedTime) => {
  const localDate = dateTime.dateLocal || dateTime.dateUtc
  const estimatedDate = estimatedTime.dateLocal || estimatedTime.dateUtc
  const isTimeChanged = estimatedDate && localDate !== estimatedDate
  return isTimeChanged
    ? { time: estimatedDate, oldTime: localDate }
    : { time: localDate }
}

/**
 * Parse flight dates strings to Luxon dates relative to the venue timezone.
 * @param {{ time: string, oldTime: string }} - times
 * @param {string} tz - venue timezone
*/
const formatToLocalDateTimes = ({ time, oldTime }, tz) =>
  ({
    localTime: getLocalGMTDate(time, tz),
    localOldTime: getLocalGMTDate(oldTime, tz)
  })

function mapFlightStatus (flightType, status, nowDate, localTime, localOldTime) {
  if (!localTime)
    return FlightLabelStatus.UNKNOWN

  if (nowDate > localTime)
    return lateFlightStatus(flightType)

  status = status.toUpperCase()
  if (status === 'C')
    return FlightLabelStatus.CANCELLED

  if (!localOldTime)
    return FlightLabelStatus.ON_TIME
  else if (localTime > localOldTime)
    return FlightLabelStatus.DELAYED
  else return FlightLabelStatus.EARLY
}

const getFlightStatusText = status => {
  switch (status) {
    case FlightLabelStatus.ON_TIME: return 'on-time'
    case FlightLabelStatus.EARLY: return 'early'
    case FlightLabelStatus.DELAYED: return 'delayed'
    case FlightLabelStatus.CANCELLED: return 'cancelled'
    case FlightLabelStatus.DEPARTED: return 'departed'
    case FlightLabelStatus.ARRIVED: return 'arrived'
    default:
      return 'unknown'
  }
}

const lateFlightStatus = flightType => flightType === FlightType.DEPARTURE
  ? FlightLabelStatus.DEPARTED
  : FlightLabelStatus.ARRIVED

const prepareAirportLabel = (flightType, departureAirport, arrivalAirport, T) =>
  flightType === FlightType.DEPARTURE
    ? T(formatAirportLabel('To'), arrivalAirport)
    : T(formatAirportLabel('From'), departureAirport)

const formatAirportLabel = prefix =>
  `flightDetails:${prefix} _city_ (_iata_)`

const prepareBaggageClaim = (claimNum, T) =>
  claimNum && T('flightDetails:Collect luggage from Baggage Claim _claimNum_', { claimNum })

/**
* Parse date string to Luxon date relative to the venue timezone.
* @param {string} date string (can be UTC)
* @param {string} tz - venue timezone
*/
const getLocalGMTDate = (date, tz) => date &&
  DateTime.fromISO(date, { zone: tz })

const getLocalFormattedDate = (dateStr, tz, locale = 'en-US') => dateStr &&
  DateTime.fromISO(dateStr, { zone: tz }).setLocale(locale).toFormat('EEE, d MMMM')

/**
* Parse date string to Luxon date in venue timezone and return short time.
* Set locale, so the returned time can match the format used in particular locale.
* E.g. for 'en-US' it will return "6:00 AM". For 'fr' it will return "6:00".
* @param {string} dateStr - date string (can be UTC)
* @param {string} tz - timezone string
* @param {string} [locale='en-US'] locale string
*/
const getLocalFormattedTime = (dateStr, tz, locale = 'en-US') => dateStr &&
  formatTime(DateTime.fromISO(dateStr, { zone: tz }), locale)
